/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// This file exports the Combobox component family alongside the
// `useComboboxAnchor` hook. Hooks and components share a file here by
// design — the hook is only meaningful in combination with the
// Combobox composition. Fast Refresh needs a component-only file, so
// this fallback to full-page reload is intentional and out of scope
// for the lint-cleanup pass.
/* eslint-disable react-refresh/only-export-components */
'use client';

import { useRef, type ComponentPropsWithRef } from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';

import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/forms/input-group';
import { cn } from '@/lib/utils';

const Combobox = ComboboxPrimitive.Root;

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
    return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
    return (
        <ComboboxPrimitive.Trigger
            data-slot="combobox-trigger"
            className={cn("text-muted-foreground transition-colors [&_svg:not([class*='size-'])]:size-4", className)}
            {...props}>
            {children}
            <ChevronDownIcon data-slot="combobox-trigger-icon" className="pointer-events-none size-4 text-current" />
        </ComboboxPrimitive.Trigger>
    );
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
    return (
        <ComboboxPrimitive.Clear
            data-slot="combobox-clear"
            render={<InputGroupButton variant="ghost" size="icon-xs" />}
            className={cn(className)}
            {...props}>
            <XIcon className="pointer-events-none" />
        </ComboboxPrimitive.Clear>
    );
}

function ComboboxInput({
    className,
    children,
    disabled = false,
    showTrigger = true,
    showClear = false,
    'aria-invalid': ariaInvalid,
    ...props
}: ComboboxPrimitive.Input.Props & {
    showTrigger?: boolean;
    showClear?: boolean;
}) {
    return (
        <InputGroup
            data-disabled={disabled ? 'true' : undefined}
            aria-disabled={disabled || undefined}
            aria-invalid={ariaInvalid || undefined}
            className={cn(
                'h-12 w-auto min-w-0 gap-2 overflow-hidden rounded-xs border-input bg-[var(--bg-input-30)] px-4 transition-[background-color,border-color,box-shadow,color,opacity] hover:bg-[var(--bg-input-50)] data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:hover:bg-[var(--bg-input-30)] data-[disabled=true]:opacity-50 focus-within:border-ring focus-within:ring-3 focus-within:ring-focus aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive-focus aria-invalid:shadow-none focus-within:aria-invalid:border-destructive focus-within:aria-invalid:ring-destructive-focus data-[disabled=true]:aria-invalid:border-input data-[disabled=true]:aria-invalid:ring-0 [&>[data-slot=input-group-control]]:h-full [&>[data-slot=input-group-control]]:px-0 [&>[data-slot=input-group-control]]:py-0 [&>[data-slot=input-group-control]]:text-sm [&>[data-slot=input-group-control]]:leading-none [&>[data-slot=input-group-control]]:placeholder:text-foreground [&>[data-slot=input-group-control]]:placeholder:opacity-100 [&>[data-slot=input-group-addon]]:gap-0 [&>[data-slot=input-group-addon]]:p-0 [&>[data-slot=input-group-addon]]:mr-0 [&>[data-slot=input-group-addon]]:text-muted-foreground [&_[data-slot=input-group-button]]:size-4 [&_[data-slot=input-group-button]]:rounded-none [&_[data-slot=input-group-button]]:p-0 [&_[data-slot=input-group-button]]:text-muted-foreground [&_[data-slot=input-group-button]]:hover:bg-transparent [&_[data-slot=input-group-button]]:hover:text-foreground [&_[data-slot=input-group-button]]:focus-visible:border-transparent [&_[data-slot=input-group-button]]:focus-visible:ring-0 [&_[data-slot=combobox-clear]]:size-4 [&_[data-slot=combobox-clear]]:rounded-none [&_[data-slot=combobox-clear]]:p-0 [&_[data-slot=combobox-clear]]:text-muted-foreground [&_[data-slot=combobox-clear]]:hover:bg-transparent [&_[data-slot=combobox-clear]]:hover:text-foreground [&_[data-slot=combobox-clear]]:focus-visible:border-transparent [&_[data-slot=combobox-clear]]:focus-visible:ring-0',
                className
            )}>
            <ComboboxPrimitive.Input
                render={<InputGroupInput disabled={disabled} className="aria-invalid:ring-0" />}
                disabled={disabled}
                aria-invalid={ariaInvalid || undefined}
                {...props}
            />
            <InputGroupAddon align="inline-end">
                {showTrigger && (
                    <InputGroupButton
                        size="icon-xs"
                        variant="ghost"
                        asChild
                        data-slot="input-group-button"
                        className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
                        disabled={disabled}>
                        <ComboboxTrigger />
                    </InputGroupButton>
                )}
                {showClear && <ComboboxClear disabled={disabled} />}
            </InputGroupAddon>
            {children}
        </InputGroup>
    );
}

function ComboboxContent({
    className,
    side = 'bottom',
    sideOffset = 6,
    align = 'start',
    alignOffset = 0,
    anchor,
    ...props
}: ComboboxPrimitive.Popup.Props &
    Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) {
    return (
        <ComboboxPrimitive.Portal>
            <ComboboxPrimitive.Positioner
                side={side}
                sideOffset={sideOffset}
                align={align}
                alignOffset={alignOffset}
                anchor={anchor}
                className="isolate z-50">
                <ComboboxPrimitive.Popup
                    data-slot="combobox-content"
                    data-chips={!!anchor}
                    className={cn(
                        'group/combobox-content relative max-h-96 w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-md bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                        className
                    )}
                    {...props}
                />
            </ComboboxPrimitive.Positioner>
        </ComboboxPrimitive.Portal>
    );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
    return (
        <ComboboxPrimitive.List
            data-slot="combobox-list"
            className={cn(
                'max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto p-1 data-empty:p-0',
                className
            )}
            {...props}
        />
    );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
    return (
        <ComboboxPrimitive.Item
            data-slot="combobox-item"
            className={cn(
                "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}>
            {children}
            <ComboboxPrimitive.ItemIndicator
                data-slot="combobox-item-indicator"
                render={
                    <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
                }>
                <CheckIcon className="pointer-events-none size-4 pointer-coarse:size-5" />
            </ComboboxPrimitive.ItemIndicator>
        </ComboboxPrimitive.Item>
    );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
    return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />;
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
    return (
        <ComboboxPrimitive.GroupLabel
            data-slot="combobox-label"
            className={cn(
                'px-2 py-1.5 text-xs text-muted-foreground pointer-coarse:px-3 pointer-coarse:py-2 pointer-coarse:text-sm',
                className
            )}
            {...props}
        />
    );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
    return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
    return (
        <ComboboxPrimitive.Empty
            data-slot="combobox-empty"
            className={cn(
                'hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-data-empty/combobox-content:flex',
                className
            )}
            {...props}
        />
    );
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
    return (
        <ComboboxPrimitive.Separator
            data-slot="combobox-separator"
            className={cn('-mx-1 my-1 h-px bg-border', className)}
            {...props}
        />
    );
}

function ComboboxChips({
    className,
    ...props
}: ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) {
    return (
        <ComboboxPrimitive.Chips
            data-slot="combobox-chips"
            className={cn(
                'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent bg-clip-padding px-2.5 py-1.5 text-sm shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-3 focus-within:ring-focus aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive-focus focus-within:aria-invalid:border-destructive focus-within:aria-invalid:ring-destructive-focus has-data-[slot=combobox-chip]:px-1.5 dark:bg-input/30',
                className
            )}
            {...props}
        />
    );
}

function ComboboxChip({
    className,
    children,
    showRemove = true,
    ...props
}: ComboboxPrimitive.Chip.Props & {
    showRemove?: boolean;
}) {
    return (
        <ComboboxPrimitive.Chip
            data-slot="combobox-chip"
            className={cn(
                'flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0',
                className
            )}
            {...props}>
            {children}
            {showRemove && (
                <ComboboxPrimitive.ChipRemove
                    render={<InputGroupButton variant="ghost" size="icon-xs" />}
                    className="-ml-1 opacity-50 hover:opacity-100"
                    data-slot="combobox-chip-remove">
                    <XIcon className="pointer-events-none" />
                </ComboboxPrimitive.ChipRemove>
            )}
        </ComboboxPrimitive.Chip>
    );
}

function ComboboxChipsInput({ className, children, ...props }: ComboboxPrimitive.Input.Props) {
    return (
        <ComboboxPrimitive.Input
            data-slot="combobox-chip-input"
            className={cn('min-w-16 flex-1 outline-none', className)}
            {...props}>
            {children}
        </ComboboxPrimitive.Input>
    );
}

function useComboboxAnchor() {
    return useRef<HTMLDivElement | null>(null);
}

export {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxClear,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
    ComboboxSeparator,
    ComboboxTrigger,
    ComboboxValue,
    useComboboxAnchor,
};
